import { injectable } from 'inversify';

import { FormatService } from '@vk-hr-tek/core/format';
import { Calendar } from '@vk-hr-tek/core/calendar';
import { Filter, FilterType } from '@vk-hr-tek/core/filter';
import { ChipColorUnion } from '@vk-hr-tek/ui/ColoredChip/ColoredChip.types';
import { Logger } from '@vk-hr-tek/core/logger';
import { t } from '@vk-hr-tek/core/translations';
import { ValidationService } from '@vk-hr-tek/core/validation';

import { EventListItem as EventListEntity, Action } from '@app/gen/events';

import { GetReviewDto } from '../dto';
import { EventUnitData } from '../types';

const STAGE_COLORS: Record<string, ChipColorUnion> = {
  completed: 'green',
  system: 'gray',
  in_paper: 'gray',
  upload: 'orange',
  upload_batch: 'orange',
  accept: 'purple',
  unep_sign: 'blue',
  unep_sign_batch: 'blue',
  ukep_sign: 'blue',
  ukep_sign_batch: 'blue',
  pep_sign: 'blue',
  pep_sign_batch: 'blue',
  default: 'gray',
  competency_eval: 'gray',
  competency_profile: 'blue',
  canceled: 'red',
  canceled_to_paper: 'gray',
  generate_document_from_template: 'orange',
  declined: 'red',
  declined_sign: 'red',
  system_booking_hook: 'gray',
  system_booking_approve: 'purple',
  system_booking_limits_exceeded_approve: 'purple',
  system_booking_trip_create: 'gray',
  system_booking_trip_info: 'gray',
  system_booking_trip_change: 'gray',
  system_booking_trip_ordering: 'blue',
  system_booking_trip_limit: 'blue',
  system_booking_trip_limit_approved: 'gray',
  system_linkedtrip_attrinfo: 'gray',
  system_linkedtrip_approveinfo: 'gray',
};

@injectable()
export class ReviewMapper {
  constructor(
    private format: FormatService,
    private calendar: Calendar,
    private logger: Logger,
    private validator: ValidationService,
  ) {}

  processEmployeeInitials(event: EventListEntity) {
    if (event.employee) {
      return `${event.employee.second_name} ${event.employee.first_name[0]}. ${event.employee.middle_name[0]}.`;
    }
    return '';
  }

  processEmployeeFullName(event: EventListEntity) {
    if (event.employee) {
      return `${event.employee.second_name} ${event.employee.first_name} ${event.employee.middle_name}`;
    }
    return '';
  }

  processEmployee(event: EventListEntity) {
    if (event.employee) {
      const units: EventUnitData[] = [];

      if (event.employee.unit) {
        units.push({
          label: 'Управленческая',
          mobileLabel: 'Упр. структура',
          value: event.employee.unit,
          color: 'blue',
          width: '50%',
        });
      }

      if (event.employee.legal_unit) {
        units.push({
          label: 'Юридическая',
          mobileLabel: 'Юр. структура',
          value: event.employee.legal_unit,
          color: 'purple',
          width: '50%',
        });
      }

      if (units.length === 1) {
        units[0].width = '100%';
      }

      return {
        id: event.employee.id,
        name: `${event.employee.second_name} ${event.employee.first_name}`,
        fullName: this.processEmployeeFullName(event),
        fullNameWithInitials: this.processEmployeeInitials(event),
        avatar: `${event.employee.second_name[0]}${event.employee.first_name[0]}`,
        personnelNumber: event.employee.personnel_number,
        birthday: event.employee.birthday,
        dismissed:
          !!event.employee.dismissed_at &&
          Date.now() > +new Date(event.employee.dismissed_at),
        dismissedAt: event.employee.dismissed_at
          ? this.format.toDate(event.employee.dismissed_at)
          : null,
        phone: event.employee.phone ?? '-',
        email: event.employee.email ?? '-',
        units: units,
        position: event.employee.position,
        unit: event.employee.unit,
        legalUnit: event.employee.legal_unit,
      };
    }
  }

  processStageName(stage: Action) {
    try {
      switch (stage.type) {
        case 'system_booking_approve':
        case 'system_booking_limits_exceeded_approve':
          return 'Согласование';
        case 'system_booking_hook':
        case 'system_linkedtrip_attrinfo':
        case 'system_linkedtrip_approveinfo':
        case 'system_booking_trip_create':
        case 'system_booking_trip_info':
        case 'system_booking_trip_change':
        case 'system_booking_trip_limit_approved':
          return 'Обработка';
        case 'system_booking_trip_ordering':
        case 'system_booking_trip_limit':
          return 'Бронирование';
        default:
          return stage.name === 'Отказ от подписания' ? 'Отказано' : stage.name;
      }
    } catch (e) {
      this.logger.error(e);
    }
  }

  processStage(event: EventListEntity) {
    try {
      if (!event.active_nodes || !event.active_nodes.length) {
        return;
      }

      const stage = event.active_nodes[0].action;

      let label = '';

      if (stage.type === 'upload') {
        label = 'Загрузить до';
      } else if (stage.type === 'accept') {
        label = 'Проверить до';
      } else if (
        stage.type === 'decline_sign' ||
        stage.type === 'declined_sign'
      ) {
        label = 'Отказано';
      } else if (
        stage.type === 'ukep_sign' ||
        stage.type === 'unep_sign' ||
        stage.type === 'ukep_sign_batch' ||
        stage.type === 'unep_sign_batch' ||
        stage.type === 'pep_sign' ||
        stage.type === 'pep_sign_batch'
      ) {
        label = 'Подписать до';
      } else if (stage.type === 'generate_document_from_template') {
        label = 'Создать до';
      }

      return {
        name: this.processStageName(stage),
        color: STAGE_COLORS[stage.type] || STAGE_COLORS.default,
        label,
      };
    } catch (e) {
      this.logger.error(e);
    }
  }

  processActiveStages(event: EventListEntity) {
    if (!event.active_nodes || event.active_nodes.length === 0) {
      return;
    }

    return event.active_nodes.map(({ action, responsible, custom_state }) => ({
      actionName:
        custom_state?.name === 'Отказ от подписания' ||
        action.name === 'Отказ от подписания'
          ? 'Отказано'
          : custom_state?.name || action.name,
      responsible,
      description: custom_state?.description,
      ...(custom_state?.color ? { color: custom_state.color } : {}),
    }));
  }

  /* eslint-disable no-magic-numbers */

  processCompanyListFilters(filters: Filter[], tags: string[] = []): Filter[] {
    if (!filters) {
      return [];
    }

    const filterByRequestNumberTooltip = t([
      ...tags.map(
        (tag) => `review.reviewList.filterByRequestNumberTooltip.${tag}`,
      ),
      'review.reviewList.filterByRequestNumberTooltip.default',
    ]);

    const availableFilters = [
      ...filters.filter((filter) => filter.type !== 'timerange'),
      ...filters.filter((filter) => filter.type === 'timerange'),
    ];

    return availableFilters
      .map((filter) => {
        if (filter.type === 'timerange') {
          return {
            ...filter,
            width: 8 as const,
            newLine: true,
            fullWidth: false,
          };
        }

        if (filter.type === 'multiple-search' && filter.key === 'unit_ids') {
          return {
            ...filter,
            width: 8 as const,
            fullWidth: false,
          };
        }

        if (filter.type === 'search' && filter.key === 'parent_event_id') {
          return {
            ...filter,
            width: 8 as const,
            disabled: true,
          };
        }

        if (
          filter.type === 'multiple-search' &&
          filter.key === 'employee_ids'
        ) {
          return {
            ...filter,
            width: 4 as const,
            twoLine: true,
            minInputValueLength: 3,
            fullWidth: false,
          };
        }

        if (filter.type === 'select' && filter.key === 'event_type_group') {
          return {
            ...filter,
            width: 4 as const,
            options: [...filter.options].sort((a, b) =>
              (a.label || '').toLowerCase() > (b.label || '').toLowerCase()
                ? 1
                : -1,
            ),
            autocomplete: true,
          };
        }

        if (filter.type === 'multiple-search' && filter.key === 'event_ids') {
          return {
            ...filter,
            tooltip: filterByRequestNumberTooltip,
          };
        }

        return {
          ...filter,
          width: 4 as const,
          fullWidth: false,
        };
      })
      .map((filter, index) => {
        if (index === 1) {
          return {
            ...filter,
            newLine: true,
          };
        }

        return filter;
      });
  }

  processListFilters(filters: Filter[], tags: string[] = []) {
    const filterByRequestNumberTooltip = t([
      ...tags.map(
        (tag) => `review.reviewList.filterByRequestNumberTooltip.${tag}`,
      ),
      'review.reviewList.filterByRequestNumberTooltip.default',
    ]);

    return filters.map((filter) => {
      if (filter.type === 'multiple-search' && filter.key === 'employee_ids') {
        return {
          ...filter,
          twoLine: true,
          minInputValueLength: 3,
          fullWidth: false,
        };
      }

      if (filter.type === 'search' && filter.key === 'parent_event_id') {
        return {
          ...filter,
          width: 12 as const,
          disabled: true,
        };
      }

      if (filter.type === 'multiple-search' && filter.key === 'unit_ids') {
        return {
          ...filter,
          fullWidth: false,
        };
      }

      if (filter.type === 'select' && filter.key === 'event_batch') {
        return {
          ...filter,
          autocomplete: true,
        };
      }

      if (filter.type === 'select' && filter.key === 'event_type_group') {
        return {
          ...filter,
          options: [...filter.options].sort((a, b) =>
            (a.label || '').toLowerCase() > (b.label || '').toLowerCase()
              ? 1
              : -1,
          ),
          autocomplete: true,
        };
      }

      if (filter.type === 'timerange') {
        return {
          ...filter,
          width: 8 as const,
          newLine: true,
          fullWidth: false,
        };
      }

      if (filter.type === 'multiple-search' && filter.key === 'event_ids') {
        return {
          ...filter,
          tooltip: filterByRequestNumberTooltip,
        };
      }

      return {
        ...filter,
        fullWidth: false,
      };
    });
  }

  processDeadline(deadline: string) {
    const durationDate = this.calendar.formatISO(
      this.calendar.subMinutes(new Date(deadline), 1),
    );

    return `${this.format.toTime(durationDate)} ${this.format.toDate(
      durationDate,
    )}`;
  }

  async processFilters(eventsDto: GetReviewDto) {
    if (!eventsDto.filters?.preset_id) {
      await this.validator.validateOrReject(eventsDto);
    }

    const filters: FilterType = {};

    for (const filter in eventsDto.filters) {
      const filterValue = eventsDto.filters[filter];
      if (filterValue === undefined || filterValue === null) {
        continue;
      }
      filters[filter] = filterValue;
    }

    if (eventsDto.tags) filters.tags = eventsDto.tags;
    if (eventsDto.companyId) {
      if (filters.company) {
        const filteredCompany = (filters.company as string[]).filter(
          (id) => id !== eventsDto.companyId,
        );
        filters.company = [...filteredCompany, eventsDto.companyId];
      } else {
        filters.company = [eventsDto.companyId];
      }
    }

    const { sortByIds } = eventsDto;
    const checkForFilterByEventIds = !!sortByIds?.length
      ? { event_ids: sortByIds }
      : {};

    const filterEventIds =
      sortByIds?.length || (filters.event_ids as string[] | undefined)?.length
        ? [
            ...(sortByIds || []),
            ...((filters.event_ids as string[] | undefined) || []),
          ]
        : undefined;

    return filters
      ? { ...filters, event_ids: filterEventIds }
      : checkForFilterByEventIds;
  }
}
